import { syncRules } from '../actions/engineActions';
import { captureException } from '@sentry/browser';
import { PUSH_TAG } from './constants';

/**
 * Handle incoming FCM push payload
 *
 * Parses the Firebase Cloud Messaging payload structure and executes
 * the appropriate action based on the message method.
 *
 * Expected payload structure:
 * ```json
 * {
 *   "data": {
 *     "message": "{\"method\":\"update_policy\"}"
 *   }
 * }
 * ```
 *
 * @param payload - FCM push payload
 */
export const handleFcmPayload = async (payload: unknown): Promise<void> => {
  try {
    const data = (payload as { data?: { message?: string } }).data;
    if (!data?.message) {
      console.warn(
        `${PUSH_TAG} Invalid payload structure - missing data.message:`,
        payload
      );
      return;
    }

    const parsedMessage = (() => {
      try {
        return JSON.parse(data.message) as { method?: string };
      } catch {
        return null;
      }
    })();

    if (parsedMessage?.method === 'update_policy') {
      console.info(
        `${PUSH_TAG} Processing update_policy request from payload:`,
        payload
      );
      await syncRules();
    } else {
      console.info(
        `${PUSH_TAG} Ignoring push notification - method not handled:`,
        parsedMessage?.method || 'unparseable',
        'from payload:',
        payload
      );
    }
  } catch (error) {
    console.error(
      `${PUSH_TAG} Error handling background message:`,
      error,
      'payload:',
      payload
    );
    captureException(error as Error);
  }
};
